import { ChangeDetectionStrategy, Component, computed, forwardRef, inject, NgZone } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { NodeViewComponent } from '@examdojo/core/tiptap';
import { StopPropagationDirective } from '@examdojo/core/stop-propagation';
import type { IconPickerSelection } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/icon-picker/icon-picker';
import type { IconPickerTab } from '@examdojo/core/tiptap-react-ui-components/components/tiptap-ui/icon-picker/icon-picker-constants';
import { IconPickerWrapperComponent } from '../accordion/icon-picker-wrapper.component';
import { getIconBgColor } from '../accordion/accordion-icon.util';

interface CustomIconNodeAttrs {
  iconType: 'fa' | 'img';
  name: string;
  color: string | null;
  showBackground: boolean;
}

@Component({
  selector: 'dojo-custom-icon-node',
  standalone: true,
  imports: [MatMenuModule, StopPropagationDirective, IconPickerWrapperComponent],
  templateUrl: './custom-icon-node.component.html',
  styleUrls: ['./custom-icon-node.component.scss'],
  providers: [
    {
      provide: NodeViewComponent,
      useExisting: forwardRef(() => CustomIconNodeComponent),
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomIconNodeComponent extends NodeViewComponent<CustomIconNodeAttrs> {
  private readonly ngZone = inject(NgZone);

  readonly iconType = this.getProp('iconType');
  readonly name = this.getProp('name');
  readonly color = this.getProp('color');
  readonly showBackground = this.getProp('showBackground');
  readonly isEditable = computed(() => this.editor().isEditable);

  readonly faClass = computed(() => `fas fa-${this.name()}`);
  readonly colorStyle = computed(() => {
    const c = this.color();
    return c && c !== 'currentColor' ? c : null;
  });
  readonly bgColor = computed(() => {
    if (!this.showBackground()) {
      return null;
    }
    return getIconBgColor(this.color() ?? undefined);
  });

  readonly initialPickerTab = computed<IconPickerTab>(() => {
    return this.iconType() === 'img' ? 'icons' : 'fontawesome';
  });
  readonly initialPickerFaColor = computed(() => {
    return this.iconType() === 'fa' ? this.color() ?? undefined : undefined;
  });
  readonly initialPickerShowBg = computed(() => {
    return this.iconType() === 'fa' ? this.showBackground() : true;
  });
  readonly activeIconValue = computed(() => this.name());

  override getDefaults = (): CustomIconNodeAttrs => ({
    iconType: 'fa',
    name: '',
    color: null,
    showBackground: false,
  });

  selectIcon(selection: IconPickerSelection): void {
    this.ngZone.run(() => {
      switch (selection.type) {
        case 'fa':
          this.updateProps({
            iconType: 'fa',
            name: selection.name,
            color: selection.color,
            showBackground: selection.showBackground ?? false,
          });
          break;
        case 'img':
          this.updateProps({
            iconType: 'img',
            name: selection.src,
            color: null,
            showBackground: false,
          });
          break;
        case 'emoji': {
          const pos = this.getPos()();
          const node = this.node();
          const editor = this.editor();
          editor
            .chain()
            .focus()
            .deleteRange({ from: pos, to: pos + node.nodeSize })
            .setEmoji(selection.emoji.name)
            .run();
          break;
        }
      }
    });
  }

  removeIcon(): void {
    this.ngZone.run(() => {
      this.deleteNode()();
    });
  }
}
